/**
 * Brouillon panier caisse — lié à la session financière et au commerçant (sessionStorage).
 * Évite qu’un panier local d’une session clôturée ou d’un autre commerçant soit repris comme vente active.
 */

/** @deprecated Clés globales — migration vers clés scopées commerçant uniquement */
export const CAISSE_PANIER_KEY = 'clickorico_caisse_panier';
export const CAISSE_PANIER_ORPHAN_KEY = 'clickorico_caisse_panier_orphan';
export const CAISSE_TICKET_KEY = 'clickorico_caisse_ticket_id';

const DRAFT_VERSION = 1;

export function resolveCaisseSessionStorageKeys(commercantId) {
  const id = String(commercantId || '').trim();
  if (!id) {
    return {
      panier: CAISSE_PANIER_KEY,
      orphan: CAISSE_PANIER_ORPHAN_KEY,
      ticket: CAISSE_TICKET_KEY,
    };
  }
  return {
    panier: `${CAISSE_PANIER_KEY}:${id}`,
    orphan: `${CAISSE_PANIER_ORPHAN_KEY}:${id}`,
    ticket: `${CAISSE_TICKET_KEY}:${id}`,
  };
}

export function resolveCaisseTicketStorageKey(commercantId) {
  return resolveCaisseSessionStorageKeys(commercantId).ticket;
}

/** Supprime les clés globales legacy (post-migration) pour éviter toute reprise fantôme. */
export function purgeLegacyGlobalCaisseStorage(storage) {
  if (!storage) return;
  try {
    storage.removeItem(CAISSE_PANIER_KEY);
    storage.removeItem(CAISSE_PANIER_ORPHAN_KEY);
    storage.removeItem(CAISSE_TICKET_KEY);
  } catch {
    /* ignore */
  }
}

function safeParseJson(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** @returns {{ lines: object[], sessionCaisseId: string|null, commercantId: string|null, ticketId: string|null, saleContext: string|null, restaurantServiceSessionId: string|null, legacy: boolean }} */
export function parseStoredPanierDraft(raw) {
  const parsed = safeParseJson(raw);
  if (!parsed) {
    return {
      lines: [],
      sessionCaisseId: null,
      commercantId: null,
      ticketId: null,
      saleContext: null,
      restaurantServiceSessionId: null,
      legacy: false,
    };
  }
  if (Array.isArray(parsed)) {
    return {
      lines: parsed,
      sessionCaisseId: null,
      commercantId: null,
      ticketId: null,
      saleContext: null,
      restaurantServiceSessionId: null,
      legacy: true,
    };
  }
  if (parsed.v === DRAFT_VERSION && Array.isArray(parsed.lines)) {
    return {
      lines: parsed.lines,
      sessionCaisseId: parsed.sessionCaisseId ?? null,
      commercantId: parsed.commercantId ?? null,
      ticketId: parsed.ticketId ?? null,
      saleContext: parsed.saleContext ?? null,
      restaurantServiceSessionId: parsed.restaurantServiceSessionId ?? null,
      legacy: false,
    };
  }
  return {
    lines: [],
    sessionCaisseId: null,
    commercantId: null,
    ticketId: null,
    saleContext: null,
    restaurantServiceSessionId: null,
    legacy: false,
  };
}

export function serializePanierDraft({
  sessionCaisseId,
  commercantId,
  lines,
  ticketId = null,
  saleContext = null,
  restaurantServiceSessionId = null,
}) {
  return JSON.stringify({
    v: DRAFT_VERSION,
    sessionCaisseId,
    commercantId,
    ticketId: ticketId || null,
    saleContext: saleContext || null,
    restaurantServiceSessionId: restaurantServiceSessionId || null,
    lines: Array.isArray(lines) ? lines : [],
  });
}

function writeOrphanToScopedKey(storage, commercantId, draft) {
  if (!commercantId) return;
  const { orphan: orphanKey } = resolveCaisseSessionStorageKeys(commercantId);
  if (!draft?.lines?.length) {
    storage.removeItem(orphanKey);
    return;
  }
  storage.setItem(
    orphanKey,
    JSON.stringify({
      v: DRAFT_VERSION,
      fromSessionCaisseId: draft.fromSessionCaisseId ?? null,
      commercantId,
      ticketId: draft.ticketId ?? null,
      lines: draft.lines,
      detectedAt: new Date().toISOString(),
    })
  );
}

/**
 * Déplace les clés globales legacy vers le scope commerçant ou en quarantaine — sans reprise cross-tenant.
 */
export function migrateLegacyGlobalPanierStorage(storage, commercantId) {
  if (!commercantId) return;
  const { panier: scopedPanier } = resolveCaisseSessionStorageKeys(commercantId);
  if (storage.getItem(scopedPanier)) return;

  const globalRaw = storage.getItem(CAISSE_PANIER_KEY);
  if (!globalRaw) return;

  const parsed = parseStoredPanierDraft(globalRaw);
  storage.removeItem(CAISSE_PANIER_KEY);

  if (!parsed.lines?.length) return;

  const ownerId = parsed.commercantId;
  if (ownerId && ownerId !== commercantId) {
    writeOrphanToScopedKey(storage, ownerId, {
      lines: parsed.lines,
      fromSessionCaisseId: parsed.sessionCaisseId,
      ticketId: parsed.ticketId,
    });
    return;
  }

  if (ownerId && ownerId === commercantId) {
    storage.setItem(
      scopedPanier,
      serializePanierDraft({
        sessionCaisseId: parsed.sessionCaisseId,
        commercantId,
        lines: parsed.lines,
        ticketId: parsed.ticketId,
        saleContext: parsed.saleContext,
        restaurantServiceSessionId: parsed.restaurantServiceSessionId,
      })
    );
    return;
  }

  // Legacy ou brouillon sans commercantId : conserver hors vente active (fail-closed cross-tenant)
  storage.setItem(
    `${CAISSE_PANIER_ORPHAN_KEY}:unowned`,
    JSON.stringify({
      v: DRAFT_VERSION,
      commercantId: null,
      fromSessionCaisseId: parsed.sessionCaisseId ?? null,
      ticketId: parsed.ticketId ?? null,
      lines: parsed.lines,
      detectedAt: new Date().toISOString(),
    })
  );
}

function migrateLegacyGlobalOrphanStorage(storage, commercantId) {
  if (!commercantId) return;
  const { orphan: scopedOrphan } = resolveCaisseSessionStorageKeys(commercantId);
  if (storage.getItem(scopedOrphan)) return;

  const globalRaw = storage.getItem(CAISSE_PANIER_ORPHAN_KEY);
  if (!globalRaw) return;

  const parsed = safeParseJson(globalRaw);
  storage.removeItem(CAISSE_PANIER_ORPHAN_KEY);
  if (!parsed || parsed.v !== DRAFT_VERSION || !Array.isArray(parsed.lines) || parsed.lines.length === 0) {
    return;
  }

  const ownerId = parsed.commercantId ?? null;
  if (ownerId && ownerId !== commercantId) {
    writeOrphanToScopedKey(storage, ownerId, {
      lines: parsed.lines,
      fromSessionCaisseId: parsed.fromSessionCaisseId,
      ticketId: parsed.ticketId,
    });
    return;
  }

  if (ownerId === commercantId || ownerId == null) {
    if (ownerId == null) {
      // Orphelin sans identité commerçant : ne pas exposer à un autre contexte
      return;
    }
    writeOrphanToScopedKey(storage, commercantId, {
      lines: parsed.lines,
      fromSessionCaisseId: parsed.fromSessionCaisseId,
      ticketId: parsed.ticketId,
    });
  }
}

export function readPanierDraft(storage, commercantId = null) {
  if (commercantId) {
    migrateLegacyGlobalPanierStorage(storage, commercantId);
    const { panier } = resolveCaisseSessionStorageKeys(commercantId);
    return parseStoredPanierDraft(storage.getItem(panier));
  }
  const raw = storage.getItem(CAISSE_PANIER_KEY);
  return parseStoredPanierDraft(raw);
}

export function readOrphanPanierDraft(storage, commercantId = null) {
  if (!commercantId) {
    const raw = storage.getItem(CAISSE_PANIER_ORPHAN_KEY);
    const parsed = safeParseJson(raw);
    if (!parsed || parsed.v !== DRAFT_VERSION || !Array.isArray(parsed.lines) || parsed.lines.length === 0) {
      return null;
    }
    return {
      lines: parsed.lines,
      fromSessionCaisseId: parsed.fromSessionCaisseId ?? null,
      commercantId: parsed.commercantId ?? null,
      ticketId: parsed.ticketId ?? null,
    };
  }

  migrateLegacyGlobalOrphanStorage(storage, commercantId);
  const { orphan } = resolveCaisseSessionStorageKeys(commercantId);
  const parsed = safeParseJson(storage.getItem(orphan));
  if (!parsed || parsed.v !== DRAFT_VERSION || !Array.isArray(parsed.lines) || parsed.lines.length === 0) {
    return null;
  }
  if (parsed.commercantId != null && parsed.commercantId !== commercantId) {
    return null;
  }
  if (parsed.commercantId == null) {
    return null;
  }
  return {
    lines: parsed.lines,
    fromSessionCaisseId: parsed.fromSessionCaisseId ?? null,
    commercantId: parsed.commercantId,
    ticketId: parsed.ticketId ?? null,
  };
}

export function writePanierDraft(storage, draft) {
  const {
    sessionCaisseId,
    commercantId,
    lines,
    ticketId = null,
    saleContext = null,
    restaurantServiceSessionId = null,
  } = draft;
  if (!sessionCaisseId || !commercantId) return;
  const { panier } = resolveCaisseSessionStorageKeys(commercantId);
  const arr = Array.isArray(lines) ? lines : [];
  if (arr.length === 0) {
    storage.removeItem(panier);
    return;
  }
  storage.setItem(
    panier,
    serializePanierDraft({
      sessionCaisseId,
      commercantId,
      lines: arr,
      ticketId,
      saleContext,
      restaurantServiceSessionId,
    })
  );
}

/** Ne pas rouvrir la vente comptoir depuis un brouillon addition restaurant. */
export function isRestaurantPanierDraft(draft) {
  if (!draft?.lines?.length) return false;
  if (draft.saleContext === 'restaurant') return true;
  if (draft.restaurantServiceSessionId) return true;
  return false;
}

export function writeOrphanPanierDraft(storage, draft) {
  const commercantId = draft?.commercantId;
  if (!commercantId) {
    storage.removeItem(CAISSE_PANIER_ORPHAN_KEY);
    return;
  }
  const { orphan } = resolveCaisseSessionStorageKeys(commercantId);
  if (!draft?.lines?.length) {
    storage.removeItem(orphan);
    return;
  }
  storage.setItem(
    orphan,
    JSON.stringify({
      v: DRAFT_VERSION,
      fromSessionCaisseId: draft.fromSessionCaisseId ?? null,
      commercantId,
      ticketId: draft.ticketId ?? null,
      lines: draft.lines,
      detectedAt: new Date().toISOString(),
    })
  );
}

export function clearPanierDraftStorage(storage, commercantId = null) {
  if (commercantId) {
    const { panier } = resolveCaisseSessionStorageKeys(commercantId);
    storage.removeItem(panier);
    return;
  }
  storage.removeItem(CAISSE_PANIER_KEY);
}

export function clearOrphanPanierDraftStorage(storage, commercantId = null) {
  if (commercantId) {
    const { orphan } = resolveCaisseSessionStorageKeys(commercantId);
    storage.removeItem(orphan);
    return;
  }
  storage.removeItem(CAISSE_PANIER_ORPHAN_KEY);
}

export function clearAllLocalPanierDrafts(storage, commercantId = null) {
  clearPanierDraftStorage(storage, commercantId);
  clearOrphanPanierDraftStorage(storage, commercantId);
}

function quarantineDraft(storage, commercantId, { lines, boundSession, ownerCommercantId, ticketId }) {
  const orphanOwner = ownerCommercantId || commercantId;
  if (!orphanOwner) {
    clearPanierDraftStorage(storage, commercantId);
    return;
  }
  writeOrphanPanierDraft(storage, {
    lines,
    fromSessionCaisseId: boundSession,
    commercantId: orphanOwner,
    ticketId,
  });
  clearPanierDraftStorage(storage, commercantId);
  try {
    if (commercantId) {
      storage.removeItem(resolveCaisseSessionStorageKeys(commercantId).ticket);
    }
    storage.removeItem(CAISSE_TICKET_KEY);
  } catch {
    /* ignore */
  }
}

function draftMatchesCommercant(stored, commercantId) {
  if (!commercantId) return false;
  if (stored.legacy) return false;
  if (!stored.commercantId) return false;
  return stored.commercantId === commercantId;
}

/**
 * À appeler lorsqu’une session financière est active (session.id connu).
 * @param {string|null|undefined} previousSessionCaisseId — undefined = premier hydratage page
 * @returns {{ activeLines: object[], orphan: object|null, sessionChanged: boolean }}
 */
export function reconcilePanierDraftForSession({
  storage,
  commercantId,
  sessionCaisseId,
  previousSessionCaisseId,
}) {
  if (!commercantId) {
    return { activeLines: [], orphan: null, sessionChanged: false };
  }

  migrateLegacyGlobalPanierStorage(storage, commercantId);
  migrateLegacyGlobalOrphanStorage(storage, commercantId);

  const stored = readPanierDraft(storage, commercantId);
  let existingOrphan = readOrphanPanierDraft(storage, commercantId);

  const sessionChanged =
    previousSessionCaisseId != null &&
    sessionCaisseId != null &&
    previousSessionCaisseId !== sessionCaisseId;

  const lines = stored.lines || [];
  const boundSession = stored.sessionCaisseId;

  if (lines.length === 0) {
    return { activeLines: [], orphan: existingOrphan, sessionChanged };
  }

  if (!draftMatchesCommercant(stored, commercantId)) {
    quarantineDraft(storage, commercantId, {
      lines,
      boundSession,
      ownerCommercantId: stored.commercantId,
      ticketId: stored.ticketId,
    });
    existingOrphan = readOrphanPanierDraft(storage, commercantId);
    return { activeLines: [], orphan: existingOrphan, sessionChanged: true };
  }

  if (boundSession && boundSession === sessionCaisseId) {
    writePanierDraft(storage, {
      sessionCaisseId,
      commercantId,
      lines,
      ticketId: stored.ticketId,
      saleContext: stored.saleContext,
      restaurantServiceSessionId: stored.restaurantServiceSessionId,
    });
    return { activeLines: lines, orphan: existingOrphan, sessionChanged };
  }

  if (boundSession && boundSession !== sessionCaisseId) {
    quarantineDraft(storage, commercantId, {
      lines,
      boundSession,
      ownerCommercantId: commercantId,
      ticketId: stored.ticketId,
    });
    existingOrphan = readOrphanPanierDraft(storage, commercantId);
    return { activeLines: [], orphan: existingOrphan, sessionChanged: true };
  }

  const initialHydration = previousSessionCaisseId === undefined;
  const reopenedAfterClose = previousSessionCaisseId === null && !initialHydration;

  if (initialHydration || previousSessionCaisseId === sessionCaisseId) {
    writePanierDraft(storage, {
      sessionCaisseId,
      commercantId,
      lines,
      ticketId: stored.ticketId,
      saleContext: stored.saleContext,
      restaurantServiceSessionId: stored.restaurantServiceSessionId,
    });
    return { activeLines: lines, orphan: existingOrphan, sessionChanged: false };
  }

  if (reopenedAfterClose || sessionChanged) {
    quarantineDraft(storage, commercantId, {
      lines,
      boundSession: null,
      ownerCommercantId: commercantId,
      ticketId: stored.ticketId,
    });
    existingOrphan = readOrphanPanierDraft(storage, commercantId);
    return { activeLines: [], orphan: existingOrphan, sessionChanged: true };
  }

  writePanierDraft(storage, {
    sessionCaisseId,
    commercantId,
    lines,
    ticketId: stored.ticketId,
    saleContext: stored.saleContext,
    restaurantServiceSessionId: stored.restaurantServiceSessionId,
  });
  return { activeLines: lines, orphan: existingOrphan, sessionChanged: false };
}

/** Total TTC estimé pour affichage accueil (lignes panier local). */
export function estimateRetailCounterDraftTotal(lines) {
  if (!Array.isArray(lines)) return 0;
  return lines.reduce((sum, line) => {
    const q = Number(line?.quantite) || 1;
    const pu = Number(line?.prixUnitaire ?? line?.prix ?? 0);
    const pt = Number(line?.prixTotal);
    if (Number.isFinite(pt) && pt > 0) return sum + pt;
    return sum + q * pu;
  }, 0);
}

/**
 * Brouillon vente comptoir actif pour la session financière courante (pas ticket serveur « en attente »).
 */
export function readActiveRetailCounterDraft(storage, { commercantId, sessionCaisseId }) {
  if (!commercantId || !sessionCaisseId) return null;
  const stored = readPanierDraft(storage, commercantId);
  if (!stored.lines?.length) return null;
  if (isRestaurantPanierDraft(stored)) return null;
  if (stored.legacy || !stored.commercantId) return null;
  if (stored.commercantId !== commercantId) return null;
  if (stored.sessionCaisseId !== sessionCaisseId) return null;
  return {
    lines: stored.lines,
    ticketId: stored.ticketId ?? null,
    totalEstimate: estimateRetailCounterDraftTotal(stored.lines),
  };
}

export function adoptOrphanDraftIntoSession(storage, { sessionCaisseId, commercantId }) {
  if (!sessionCaisseId || !commercantId) return null;
  const orphan = readOrphanPanierDraft(storage, commercantId);
  if (!orphan?.lines?.length) return null;
  if (orphan.commercantId !== commercantId) return null;
  writePanierDraft(storage, {
    sessionCaisseId,
    commercantId,
    lines: orphan.lines,
    ticketId: orphan.ticketId,
  });
  clearOrphanPanierDraftStorage(storage, commercantId);
  return orphan.lines;
}

/** Réinitialise l’état React/storage visible lors d’un changement de commerçant (impersonation). */
export function resetCaisseLocalStateForCommercantSwitch(storage, previousCommercantId, nextCommercantId) {
  if (previousCommercantId && previousCommercantId !== nextCommercantId) {
    clearPanierDraftStorage(storage, previousCommercantId);
    try {
      storage.removeItem(resolveCaisseSessionStorageKeys(previousCommercantId).ticket);
    } catch {
      /* ignore */
    }
  }
}
